/**
 * BSA/AML risk assessment — CLARID-SPEC-BSA-001 §3.3.
 *
 * The screen where deviation-requires-justification actually confronts someone.
 *
 * Design consequences of that, all deliberate:
 *
 *   - A factor's REFERENCE BASELINE is shown beside its rating, always. You
 *     cannot argue with guidance you can't see.
 *   - Rating below the baseline reveals a required rationale field inline, with
 *     the reason stated. It is not a modal, not a toast, not a validation error
 *     on submit — it appears at the moment of the decision.
 *   - Finalize is disabled while any justification is missing, and says exactly
 *     which factors are blocking rather than "fix errors".
 *   - Factors start INACTIVE. The assessment opens as a question — "do you do
 *     these?" — not as an assertion about a bank we've never met.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { bsaApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';

type RiskTier = 'LOW' | 'MODERATE_LOW' | 'MODERATE' | 'MODERATE_HIGH' | 'HIGH';
type ControlRating = 'STRONG' | 'ADEQUATE' | 'NEEDS_IMPROVEMENT' | 'WEAK' | 'NOT_ASSESSED';

const TIERS: RiskTier[] = ['LOW', 'MODERATE_LOW', 'MODERATE', 'MODERATE_HIGH', 'HIGH'];
const RANK: Record<RiskTier, number> = { LOW: 0, MODERATE_LOW: 1, MODERATE: 2, MODERATE_HIGH: 3, HIGH: 4 };
const TIER_LABEL: Record<RiskTier, string> = {
  LOW: 'Low', MODERATE_LOW: 'Moderate-low', MODERATE: 'Moderate',
  MODERATE_HIGH: 'Moderate-high', HIGH: 'High',
};
const TIER_CLS: Record<RiskTier, string> = {
  LOW: 'bg-emerald-100 text-emerald-800',
  MODERATE_LOW: 'bg-lime-100 text-lime-800',
  MODERATE: 'bg-amber-100 text-amber-800',
  MODERATE_HIGH: 'bg-orange-100 text-orange-800',
  HIGH: 'bg-red-100 text-red-800',
};
const CONTROLS: ControlRating[] = ['NOT_ASSESSED', 'WEAK', 'NEEDS_IMPROVEMENT', 'ADEQUATE', 'STRONG'];
const CONTROL_LABEL: Record<ControlRating, string> = {
  NOT_ASSESSED: 'Not assessed', WEAK: 'Weak', NEEDS_IMPROVEMENT: 'Needs improvement',
  ADEQUATE: 'Adequate', STRONG: 'Strong',
};
const PILLAR_LABEL: Record<string, string> = {
  PRODUCTS_SERVICES: 'Products & services',
  CUSTOMERS_ENTITIES: 'Customers & entities',
  GEOGRAPHIES: 'Geographies',
  TRANSACTIONS_CHANNELS: 'Transactions & channels',
};

interface Factor {
  id: string;
  pillar: string;
  label: string;
  citation: string | null;
  isActive: boolean;
  referenceTier: RiskTier | null;
  inherentTier: RiskTier | null;
  controlRating: ControlRating;
  residualTier: RiskTier | null;
  overrideTier: RiskTier | null;
  overrideRationale: string | null;
  volumeEscalates: boolean;
}

const TierPill = ({ tier }: { tier: RiskTier | null }) =>
  tier ? (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${TIER_CLS[tier]}`}>
      {TIER_LABEL[tier]}
    </span>
  ) : (
    <span className="text-xs text-gray-400">—</span>
  );

export default function BsaRiskAssessmentPage() {
  const { teamId } = useTeamId();
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['bsa-assessment', teamId, id],
    queryFn: async () => (await bsaApi.assessments.get(teamId!, id!)).data,
    enabled: !!teamId && !!id,
  });

  const patch = useMutation({
    mutationFn: async (v: { factorId: string; data: Record<string, unknown> }) =>
      (await bsaApi.factors.update(teamId!, v.factorId, v.data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bsa-assessment', teamId, id] }),
  });

  const finalize = useMutation({
    mutationFn: async () => (await bsaApi.assessments.finalize(teamId!, id!)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bsa-assessment', teamId, id] }),
  });

  const assessment = data?.assessment;
  const rollup = data?.rollup;
  const isFinalized = assessment?.status === 'FINALIZED';

  // Memoized so the `?? []` fallback doesn't produce a new array identity every
  // render and invalidate the grouping below.
  const factors: Factor[] = useMemo(() => assessment?.factors ?? [], [assessment]);

  const blocking = useMemo(
    () => new Set<string>((rollup?.unjustifiedDeviations ?? []).map((d: { id: string }) => d.id)),
    [rollup],
  );

  const byPillar = useMemo(() => {
    const m = new Map<string, Factor[]>();
    for (const f of factors) {
      const arr = m.get(f.pillar) ?? [];
      arr.push(f);
      m.set(f.pillar, arr);
    }
    return m;
  }, [factors]);

  if (isLoading) return <div className="py-20 text-center text-sm text-gray-500">Loading…</div>;
  if (!assessment) return <div className="py-20 text-center text-sm text-gray-500">Not found.</div>;

  const activeCount = factors.filter((f) => f.isActive).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">{assessment.title}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {activeCount} of {factors.length} factors confirmed as activities this institution performs.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wide text-gray-500">Residual risk</div>
            <div className="mt-0.5"><TierPill tier={rollup?.residualRiskTier ?? null} /></div>
          </div>
          {!isFinalized && (
            <button
              type="button"
              onClick={() => finalize.mutate()}
              disabled={!rollup?.canFinalize || finalize.isPending}
              title={
                rollup?.canFinalize
                  ? 'Finalize this assessment'
                  : 'Ratings below published guidance need a documented reason first'
              }
              className="rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {finalize.isPending ? 'Finalizing…' : 'Finalize'}
            </button>
          )}
          {isFinalized && (
            <span className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-600">
              <LockClosedIcon className="h-4 w-4" aria-hidden /> Finalized
            </span>
          )}
        </div>
      </div>

      {/* Blocking banner — names the factors rather than saying "fix errors". */}
      {rollup && !rollup.canFinalize && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="flex items-start gap-2.5">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0 mt-0.5 text-amber-600" aria-hidden />
            <div>
              <p className="text-sm font-medium text-gray-900">
                {rollup.unjustifiedDeviations.length} rating
                {rollup.unjustifiedDeviations.length === 1 ? '' : 's'} below published guidance need a reason
              </p>
              <p className="mt-1 text-xs text-gray-700">
                Examiners treat the FFIEC Manual as a baseline. Rating something lower than the
                guidance is allowed — it just has to be explained and documented at the time.
              </p>
              <ul className="mt-2 space-y-0.5 text-xs text-gray-800">
                {rollup.unjustifiedDeviations.map((d: { id: string; label: string; referenceTier: RiskTier; overrideTier: RiskTier }) => (
                  <li key={d.id}>
                    <span className="font-medium">{d.label}</span> — guidance says{' '}
                    {TIER_LABEL[d.referenceTier]}, rated {TIER_LABEL[d.overrideTier]}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {rollup?.canFinalize && !isFinalized && activeCount > 0 && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800 flex items-center gap-2">
          <CheckCircleIcon className="h-4 w-4" aria-hidden />
          Every rating either matches published guidance or carries a documented reason.
        </div>
      )}

      {/* Pillars */}
      {[...byPillar.entries()].map(([pillar, list]) => {
        const p = rollup?.pillars?.find((x: { pillar: string }) => x.pillar === pillar);
        return (
          <section key={pillar} className="rounded-lg border border-gray-200 bg-white overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2.5">
              <h2 className="text-sm font-semibold text-gray-900">{PILLAR_LABEL[pillar] ?? pillar}</h2>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                {p?.activeFactorCount ?? 0} active
                <TierPill tier={p?.residualTier ?? null} />
              </div>
            </div>

            <ul className="divide-y divide-gray-100">
              {list.map((f) => {
                const effective = f.overrideTier ?? f.residualTier;
                const isDownward =
                  !!f.overrideTier && !!f.referenceTier && RANK[f.overrideTier] < RANK[f.referenceTier];
                const needsReason = blocking.has(f.id);

                return (
                  <li key={f.id} className={`px-4 py-3 ${needsReason ? 'bg-amber-50/40' : ''}`}>
                    <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
                      <label className="flex items-start gap-2 min-w-0 flex-1">
                        <input
                          type="checkbox"
                          checked={f.isActive}
                          disabled={isFinalized}
                          onChange={(e) =>
                            patch.mutate({ factorId: f.id, data: { isActive: e.target.checked } })
                          }
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-500"
                        />
                        <span className="min-w-0">
                          <span className={`text-sm ${f.isActive ? 'text-gray-900' : 'text-gray-400'}`}>
                            {f.label}
                          </span>
                          {f.citation && (
                            <span className="block text-[11px] text-gray-400">{f.citation}</span>
                          )}
                        </span>
                      </label>

                      {f.isActive && (
                        <>
                          {/* The baseline, always visible. */}
                          <div className="shrink-0">
                            <div className="text-[10px] uppercase tracking-wide text-gray-400">Guidance</div>
                            <TierPill tier={f.referenceTier} />
                          </div>

                          <div className="shrink-0">
                            <div className="text-[10px] uppercase tracking-wide text-gray-400">Controls</div>
                            <select
                              value={f.controlRating}
                              disabled={isFinalized}
                              onChange={(e) =>
                                patch.mutate({ factorId: f.id, data: { controlRating: e.target.value } })
                              }
                              className="mt-0.5 rounded border-gray-300 text-xs focus:border-gray-500 focus:ring-gray-500"
                            >
                              {CONTROLS.map((c) => (
                                <option key={c} value={c}>{CONTROL_LABEL[c]}</option>
                              ))}
                            </select>
                          </div>

                          <div className="shrink-0">
                            <div className="text-[10px] uppercase tracking-wide text-gray-400">Residual</div>
                            <select
                              value={f.overrideTier ?? effective ?? ''}
                              disabled={isFinalized}
                              onChange={(e) =>
                                patch.mutate({
                                  factorId: f.id,
                                  data: { overrideTier: e.target.value || null },
                                })
                              }
                              className="mt-0.5 rounded border-gray-300 text-xs focus:border-gray-500 focus:ring-gray-500"
                            >
                              {TIERS.map((t) => (
                                <option key={t} value={t}>{TIER_LABEL[t]}</option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}
                    </div>

                    {/* The justification field appears at the moment of the
                        decision, not as a validation error after the fact. */}
                    {f.isActive && isDownward && (
                      <div className="mt-2.5 rounded-md border border-amber-200 bg-white px-3 py-2.5">
                        <label className="block text-xs font-medium text-gray-900">
                          Why is this lower than the {TIER_LABEL[f.referenceTier!].toLowerCase()} risk the
                          guidance indicates?
                        </label>
                        <p className="mt-0.5 text-[11px] text-gray-500">
                          An examiner will ask. Recording it now is what makes the rating defensible.
                        </p>
                        <textarea
                          rows={2}
                          disabled={isFinalized}
                          defaultValue={f.overrideRationale ?? ''}
                          onChange={(e) => setDrafts((d) => ({ ...d, [f.id]: e.target.value }))}
                          onBlur={() => {
                            const v = drafts[f.id];
                            if (v !== undefined && v !== (f.overrideRationale ?? '')) {
                              patch.mutate({ factorId: f.id, data: { overrideRationale: v } });
                            }
                          }}
                          placeholder="e.g. Three NBFI relationships, all check cashers under $50k monthly, EDD applied and reviewed quarterly."
                          className="mt-1.5 w-full rounded border-gray-300 text-xs focus:border-gray-500 focus:ring-gray-500"
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
